import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Tooltip, Legend, Filler,
} from "chart.js";
import Layout from "../../components/Layout";
import { api } from "./api";
import "./AdminDashboard.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const RANGES = [7, 30, 90];
const fmtUsd = (n) => `$${(n || 0).toFixed(2)}`;
const fmtNum = (n) => (n || 0).toLocaleString();
const dayTokens = (d) => d.input_tokens + d.output_tokens;

const cssVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

export default function ChatbotAdminDashboard() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState("tokens"); // "tokens" | "usd"
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    api.adminUsage(days)
      .then((d) => { if (live) setUsage(d); })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [days]);

  const byDay = usage?.by_day || [];
  const byUser = usage?.by_user || [];
  const byChatbot = usage?.by_chatbot || [];
  const totals = usage?.totals || {};

  const accent = cssVar("--ca-accent", "#3469ff");
  const tickColor = cssVar("--ca-text-muted", "#9ca3af");
  const gridColor = cssVar("--ca-border", "#e5e7eb");

  const chartData = {
    labels: byDay.map((d) => d.date),
    datasets: [{
      label: metric === "usd" ? "USD" : "Tokens",
      data: byDay.map((d) => (metric === "usd" ? d.cost_usd : dayTokens(d))),
      borderColor: accent,
      backgroundColor: "rgba(52,105,255,0.12)",
      pointBackgroundColor: accent,
      fill: true, tension: 0.25, pointRadius: 2,
    }],
  };
  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: tickColor, maxRotation: 0, autoSkip: true }, grid: { color: gridColor } },
      y: {
        ticks: {
          color: tickColor,
          callback: (v) => metric === "usd"
            ? `$${v}`
            : Intl.NumberFormat("en", { notation: "compact" }).format(v),
        },
        grid: { color: gridColor },
      },
    },
  };

  return (
    <Layout>
      <div className="chad-page">
        <div className="chad-inner">
          <header className="chad-head">
            <button className="ca-btn ca-btn--ghost ca-btn--sm" onClick={() => navigate(-1)}>← Back</button>
            <h1>Chatbot Usage Dashboard</h1>
          </header>

          {error && <div className="chad-error">{error}</div>}

          <div className="chad-controls">
            <div className="chad-seg">
              {RANGES.map((r) => (
                <button key={r} className={days === r ? "active" : ""}
                        onClick={() => setDays(r)}>{r}d</button>
              ))}
            </div>
            <div className="chad-seg">
              <button className={metric === "tokens" ? "active" : ""}
                      onClick={() => setMetric("tokens")}>Tokens</button>
              <button className={metric === "usd" ? "active" : ""}
                      onClick={() => setMetric("usd")}>USD</button>
            </div>
          </div>

          <section className="chad-cards">
            <Card label="Total tokens" value={fmtNum((totals.input_tokens || 0) + (totals.output_tokens || 0))} />
            <Card label="Total cost" value={fmtUsd(totals.cost_usd)} />
            <Card label="Messages" value={fmtNum(totals.message_count)} />
            <Card label="Active users" value={fmtNum(totals.active_users)} />
          </section>

          <section className="chad-chart">
            {loading ? <div className="chad-chart-empty">Loading…</div>
              : byDay.length === 0 ? <div className="chad-chart-empty">No usage in this range.</div>
              : <Line data={chartData} options={chartOptions} />}
          </section>

          <section className="chad-users">
            <h2>By user</h2>
            <div className="chad-table-wrap">
              <table>
                <thead>
                  <tr><th>User</th><th>Messages</th><th>In</th><th>Out</th><th>Cost</th><th>Last active</th></tr>
                </thead>
                <tbody>
                  {byUser.map((u) => (
                    <tr key={u.user_email}
                        className={selectedUser === u.user_email ? "selected" : ""}
                        onClick={() => setSelectedUser(u.user_email)}>
                      <td>{u.user_email}</td>
                      <td>{fmtNum(u.message_count)}</td>
                      <td>{fmtNum(u.input_tokens)}</td>
                      <td>{fmtNum(u.output_tokens)}</td>
                      <td>{fmtUsd(u.cost_usd)}</td>
                      <td>{u.last_active}</td>
                    </tr>
                  ))}
                  {byUser.length === 0 && !loading && (
                    <tr><td colSpan={6} className="chad-empty">No users yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="chad-chatbots">
            <h2>By chatbot</h2>
            <div className="chad-table-wrap">
              <table>
                <thead>
                  <tr><th>Chatbot</th><th>Messages</th><th>In</th><th>Out</th>
                      <th>Cost</th><th>Active users</th><th>Last used</th></tr>
                </thead>
                <tbody>
                  {byChatbot.map((c) => (
                    <tr key={c.chatbot_id || c.chatbot_name}>
                      <td>{c.chatbot_name}</td>
                      <td>{fmtNum(c.message_count)}</td>
                      <td>{fmtNum(c.input_tokens)}</td>
                      <td>{fmtNum(c.output_tokens)}</td>
                      <td>{fmtUsd(c.cost_usd)}</td>
                      <td>{fmtNum(c.active_users)}</td>
                      <td>{c.last_active}</td>
                    </tr>
                  ))}
                  {byChatbot.length === 0 && !loading && (
                    <tr><td colSpan={7} className="chad-empty">No chatbot usage yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {selectedUser && (
            <MessagesPanel userEmail={selectedUser} onClose={() => setSelectedUser(null)} />
          )}
        </div>
      </div>
    </Layout>
  );
}

function Card({ label, value }) {
  return (
    <div className="chad-card">
      <div className="chad-card-value">{value}</div>
      <div className="chad-card-label">{label}</div>
    </div>
  );
}

function MessagesPanel({ userEmail, onClose }) {
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback((reset) => {
    setLoading(true); setError(null);
    api.adminMessages(userEmail, { q, before: reset ? null : nextBefore })
      .then((d) => {
        setMessages((prev) => (reset ? d.messages : [...prev, ...d.messages]));
        setNextBefore(d.next_before);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [userEmail, q, nextBefore]);

  useEffect(() => {
    const t = setTimeout(() => load(true), 250);
    return () => clearTimeout(t);
    // load is intentionally omitted: it's recreated on every nextBefore change via
    // useCallback, but this effect should only re-fire on a genuine user/query change,
    // not on pagination state — "Load more" calls load(false) directly instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, q]);

  return (
    <section className="chad-messages">
      <div className="chad-messages-head">
        <h2>Prompts — {userEmail}</h2>
        <button className="ca-btn ca-btn--icon" onClick={onClose} aria-label="Close">×</button>
      </div>
      <input className="ca-input chad-search" placeholder="Search prompts…"
             value={q} onChange={(e) => setQ(e.target.value)} />
      {error && <div className="chad-error">{error}</div>}
      <ul className="chad-msg-list">
        {messages.map((m, i) => (
          <li key={i}>
            <div className="chad-msg-text">{m.text}</div>
            <div className="chad-msg-meta">
              {m.timestamp ? new Date(m.timestamp).toLocaleString() : ""} · {m.chatbot_name}
            </div>
          </li>
        ))}
        {messages.length === 0 && !loading && <li className="chad-empty">No prompts.</li>}
      </ul>
      {nextBefore && (
        <button className="ca-btn ca-btn--sm chad-more" onClick={() => load(false)} disabled={loading}>
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </section>
  );
}
